/**
 * The answer key's validity verdict, as every screen that shows a score reads
 * it.
 *
 * WHAT AN "ANSWER KEY" IS. Every assessment scenario carries a reviewed set of
 * expected answers -- which headline moved which instrument, and how far -- and
 * a candidate's score is the distance between their calls and that key. The
 * code and the API call it a "benchmark"; on screen it is the ANSWER KEY,
 * because in a trading room a benchmark is an index, and the first thing that
 * happened when a trainer read "scored against a benchmark" was that they
 * thought of the S&P.
 *
 * WHAT THE QUALITY AUDIT IS. Before an answer key is allowed to grade anyone,
 * the audit measures whether it can tell skill from clicking: what a candidate
 * who does nothing scores, whether the key's own convictions pointed the way
 * the market actually went, whether the scored instruments are all covered.
 * The audit produces a verdict -- pass, warn, insufficient evidence, or fail --
 * with one sentence per check saying what it found.
 *
 * WHY THIS FILE EXISTS. That verdict was computed, stored and explained well,
 * and then stayed inside one admin drawer. Trainers were reading confident
 * hiring language ("85+ strong hire signal") off scores produced by an answer
 * key the audit had failed, and nothing anywhere said so. This module is the
 * single reader of that verdict, so the trainer report, the admin results
 * screen, the launch modal and the attach picker cannot disagree about whether
 * a number may be acted on.
 *
 * THE CONTRACT. Four producers already serve this fact, under three key names,
 * and one reader folds them:
 *
 *   `validity`            the LINK SNAPSHOT stamped at attach and served with
 *                         every result (`link_validity_snapshot`,
 *                         pm/assessment/services/attach_gate.py) -- the record
 *                         of what was true when the session was graded. Also
 *                         the benchmark rollup's per-scenario cell.
 *   `answer_key_gate`     the same document under its product name.
 *   `activation_verdict`  `activation_verdict_summary`
 *                         (pm/assessment/admin_views.py), which nests the whole
 *                         verdict under `validity`.
 *   `{verdict: {...}}`    the admin validity endpoint, whose document is nested
 *                         and whose freshness sits outside it.
 *
 * Inside, the field names are the backend's own. The snapshot spells things
 * more precisely than the raw verdict does and those spellings win where both
 * appear: `scenario_code` for the code, `verdict_current` for freshness,
 * `failing_checks` / `caution_checks` for the sentences, `headline` for the
 * line the backend authored for a trader, and `rating_is_evidence` for the one
 * question a report has to answer -- may a hiring band be printed at all.
 *
 * ABSENT IS NOT THE SAME AS EMPTY, and the difference decides what a person is
 * told. A response with no gate key at all means this screen did not ask -- it
 * renders nothing, because announcing "no check on record" when nobody looked
 * is a lie. A response whose gate key is present and empty means the check
 * genuinely is not on record, which is worth saying out loud: most answer keys
 * in service were activated before the audit existed.
 */

/** One check the audit ran, with the sentence it wrote about what it found. */
export type AnswerKeyCheck = {
  key: string;
  verdict: string;
  detail?: string;
  value?: unknown;
  threshold?: unknown;
  examined?: number | null;
  vacuous?: boolean;
};

/** The raw document as any of the endpoints serve it. Every field optional:
 *  this is read defensively because four different producers write it. */
export type AnswerKeyGateDocument = {
  code?: string | null;
  scenario?: string | null;
  scenario_code?: string | null;
  name?: string | null;
  scenario_name?: string | null;
  /** Why a snapshot exists: an attach, a launch, or a reconstruction. */
  snapshot_reason?: string | null;
  /** The sentence the backend authored for a trainer to read. */
  headline?: string | null;
  /** May a rating band be printed from these scores at all. */
  rating_is_evidence?: boolean | null;
  /** false = the verdict grades different content than what is stored now. */
  verdict_current?: boolean | null;
  failing_checks?: AnswerKeyCheck[] | null;
  caution_checks?: AnswerKeyCheck[] | null;
  pass_bar?: number | null;
  pass_bar_sentence?: string | null;
  launch_blocked?: boolean | null;
  launch_block_reason?: string | null;
  verdict?: string | null;
  blocking?: boolean | null;
  /** false = the verdict graded different content (the admin screens' STALE). */
  current?: boolean | null;
  stale?: boolean | null;
  recorded?: boolean | null;
  snapshot_recorded?: boolean | null;
  overridden?: boolean | null;
  overridden_by?: string | null;
  overridden_at?: string | null;
  override_reason?: string | null;
  gate_version?: string | null;
  computed_at?: string | null;
  failed_checks?: string[] | null;
  checks?: AnswerKeyCheck[] | null;
  /**
   * Scored instruments the trainee's Markets board cannot draw
   * (`attach_gate.link_validity_snapshot`). `checked: false` means NOBODY
   * LOOKED, which is not the same fact as nothing being wrong.
   */
  instrument_visibility?: {
    checked?: boolean | null;
    ok?: boolean | null;
    problems?: string[] | null;
  } | null;
  /**
   * RETIREMENT, AS OF NOW (D8). An answer key is never deleted: when its
   * family is regenerated the previous key is RETIRED, and every session it
   * already graded keeps producing the result it produced. So the verdict on
   * this document still describes the grading honestly, while `key_status_now`
   * describes the key TODAY -- two different facts, both worth saying.
   * `key_retired_sentence` is the backend's own wording where it sends one,
   * and it wins: the screen and the API must not narrate the same event twice
   * in two voices.
   */
  key_status_now?: string | null;
  key_retired?: boolean | null;
  key_retired_at?: string | null;
  key_superseded_by?: string | null;
  key_retired_sentence?: string | null;
  /** `activation_verdict_summary` nests the whole verdict payload here. */
  validity?: AnswerKeyGateDocument | null;
};

/**
 * What the screen switches on.
 *
 * - `blocked`     the audit failed and nobody signed for it. Scores may not
 *                 carry a rating band, and the hire-signal legend is withheld.
 * - `overridden`  the audit failed and a named admin took responsibility, in
 *                 writing. The scores stand; the record is shown beside them.
 * - `caution`     a warning, which never blocks. Said once, quietly.
 * - `unrecorded`  no quality audit on record for this answer key at all.
 * - `clear`       passed. Nothing is rendered: a banner nobody needs is noise,
 *                 and noise is how people learn to skip banners.
 */
export type AnswerKeyGateState =
  | 'blocked'
  | 'overridden'
  | 'caution'
  | 'unrecorded'
  | 'clear';

export type AnswerKeyGate = {
  state: AnswerKeyGateState;
  /** The answer key's stable code, when the payload named it. */
  code: string;
  /** The backend's own sentence for a trainer, when it wrote one. */
  headline: string;
  /** How the pass bar for this answer key was set, in one sentence. */
  passBarSentence: string;
  /** An admin has taken this answer key out of service for new launches. */
  launchBlocked: boolean;
  launchBlockReason: string;
  verdict: string;
  blocking: boolean;
  overridden: boolean;
  overriddenBy: string;
  overriddenAt: string;
  overrideReason: string;
  gateVersion: string;
  computedAt: string;
  /** The verdict graded a different version of this answer key. */
  stale: boolean;
  /** The checks that produced this state, with their own sentences. */
  failingChecks: AnswerKeyCheck[];
  /**
   * Instruments this candidate was marked on and could not see on their
   * Markets screen, each in the backend's own sentence. Empty when there are
   * none and when nobody checked.
   */
  invisibleInstruments: string[];
  /**
   * The rating band ("Positive / coachable") must be replaced by
   * "Indicative only — not rated" while this is true.
   */
  suppressRating: boolean;
  /**
   * The "85+ strong hire signal" legend must not render while this is true.
   * It is withheld for an unchecked answer key as well as a failed one: the
   * legend is a hiring claim, and an unmeasured key cannot support it. The
   * numbers themselves are still shown -- unmeasured is not the same as wrong.
   */
  suppressLegend: boolean;
  /**
   * This answer key is out of service: its family was regenerated and a new
   * key took its place. It is NOT a finding against the scores -- the key was
   * fit when it graded them -- so nothing is suppressed. It is said out loud
   * because a reader who goes looking for this key on the blueprint page will
   * find its replacement, with different numbers, and needs to know why.
   */
  retired: boolean;
  retiredAt: string;
  supersededBy: string;
  /** The one sentence for that fact, empty when the key is still in service. */
  retiredNote: string;
};

/** Verdicts that make an answer key unfit to grade (BLOCKING_VERDICTS). */
const BLOCKING_VERDICTS = ['fail', 'insufficient'];

const text = (value: unknown): string =>
  typeof value === 'string' ? value : value == null ? '' : String(value);

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The keys a payload may carry the audit under, canonical first. */
const GATE_KEYS = ['answer_key_gate', 'validity', 'activation_verdict'];

/**
 * Human names for the audit's check keys. The dotted key is still shown beside
 * the name, because it is what an admin searches for and what the backend logs.
 */
const CHECK_LABELS: Record<string, string> = {
  'no_skill.floor': 'No-skill floor',
  'no_skill.separation': 'Score separation',
  'predictive.hit_rate': 'Direction hit rate',
  'predictive.correlation': 'Agreement with realised moves',
  'predictive.counter_signal': 'Instruments pointing the wrong way',
  'coverage.macro_clean': 'Dates left in the macro text',
  'coverage.instruments': 'Scored instrument coverage',
  'coverage.periods': 'Period coverage',
  'release.identity': 'Answer key identity',
};

/** "no_skill.floor" -> "No-skill floor"; anything unknown reads as a phrase. */
export const checkLabel = (key: string): string => {
  if (CHECK_LABELS[key]) return CHECK_LABELS[key];
  const tail = key.split('.').pop() || key;
  const words = tail.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
};

const asChecks = (value: unknown): AnswerKeyCheck[] =>
  Array.isArray(value)
    ? value.filter(isObject).map(row => ({
        key: text(row.key),
        verdict: text(row.verdict).toLowerCase(),
        detail: text(row.detail),
        value: row.value,
        threshold: row.threshold,
        examined: typeof row.examined === 'number' ? row.examined : null,
        vacuous: Boolean(row.vacuous),
      }))
    : [];

/**
 * Pull the audit document out of whatever the caller has -- a results response,
 * a session link row, a scenario row, or the document itself.
 *
 * Returns `undefined` when no audit key is present (this screen did not ask) and
 * `null` when an audit key is present but empty (there is genuinely no check on
 * record). The two are different sentences on screen.
 */
const locate = (source: unknown): AnswerKeyGateDocument | null | undefined => {
  if (!isObject(source)) return undefined;
  for (const key of GATE_KEYS) {
    if (!(key in source)) continue;
    const value = source[key];
    // A key present but undefined is a caller passing through a field the
    // endpoint never sent (the redux slice keeps the key either way), which is
    // "did not ask" -- not "no check on record".
    if (value === undefined) return undefined;
    if (isObject(value) && Object.keys(value).length > 0) {
      return value as AnswerKeyGateDocument;
    }
    return null;
  }
  // The validity endpoints answer `{scenario, current, blocking, verdict}`:
  // the document is nested under `verdict` while the freshness and the veto
  // sit outside it. A null `verdict` there is the endpoint saying this answer
  // key has never been through the audit -- which is a fact worth showing, not
  // an absence.
  if ('verdict' in source && !isObject(source.verdict)) {
    if (source.verdict === null && 'blocking' in source) return null;
  }
  if ('verdict' in source && isObject(source.verdict)) {
    const inner = source.verdict as AnswerKeyGateDocument;
    // THE ENVELOPE MAY ADD A VETO, NEVER REMOVE ONE.
    //
    // The endpoint's outer `blocking` is `blocks_activation(current_verdict)`
    // (pm/assessment/admin_views.py `validity`), and a STALE answer key has no
    // current verdict, so it answers FALSE -- over a document whose own
    // verdict is `fail`. Letting the outer value win turned a failed grading
    // into a caution and printed "Strong hire signal" over it. So: OR, not
    // override. Both absent stays absent, so the verdict word still decides.
    const envelopeBlocking = source.blocking as boolean | null | undefined;
    const blocking =
      envelopeBlocking == null && inner.blocking == null
        ? inner.blocking
        : Boolean(envelopeBlocking) || Boolean(inner.blocking);
    return {
      ...inner,
      code: (source.scenario as string) || inner.code,
      current:
        source.current != null ? (source.current as boolean) : inner.current,
      blocking,
      recorded: true,
    };
  }
  // The caller handed the document itself.
  if ('verdict' in source || 'snapshot_recorded' in source) {
    return source as AnswerKeyGateDocument;
  }
  return undefined;
};

/**
 * The retirement sentence, when the backend did not write one of its own.
 *
 * Deliberately about the SESSION, not about the key: the person reading is
 * looking at a score, and what they need to know is which key produced it and
 * that the key is no longer the one on the blueprint page.
 */
const retirementSentence = (retiredAt: string, supersededBy: string): string => {
  const when = retiredAt ? new Date(retiredAt) : null;
  const day =
    when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString() : '';
  const opening = day
    ? `Graded against an answer key retired on ${day}.`
    : 'Graded against an answer key that has since been retired.';
  return supersededBy
    ? `${opening} It was replaced by ${supersededBy}, which grades different content.`
    : `${opening} The scores stand; the key is no longer used for new sessions.`;
};

/**
 * Normalise a payload into the one shape every banner reads.
 * `null` means "render nothing": the screen was never given the fact.
 */
export const readAnswerKeyGate = (source: unknown): AnswerKeyGate | null => {
  const located = locate(source);
  if (located === undefined) return null;

  const document: AnswerKeyGateDocument = located || {};
  // `activation_verdict_summary` keeps the headline facts at the top level and
  // the full verdict (with its checks) nested; read the nested one for the
  // sentences and let the top level win where both spell the same fact.
  const nested = isObject(document.validity)
    ? (document.validity as AnswerKeyGateDocument)
    : {};

  const pick = <K extends keyof AnswerKeyGateDocument>(key: K) =>
    document[key] != null ? document[key] : nested[key];

  const verdict = text(pick('verdict')).toLowerCase();
  const recordedFlag =
    document.recorded != null
      ? Boolean(document.recorded)
      : document.snapshot_recorded != null
        ? Boolean(document.snapshot_recorded)
        : null;
  const recorded = recordedFlag != null ? recordedFlag : Boolean(verdict);

  // The VERDICT WORD is the ground truth and a `blocking` flag may only add to
  // it. Every producer computes that flag against TODAY, so each of them
  // answers false for a grading that no longer describes the stored content --
  // which is a statement about freshness, not a finding that the answer key is
  // fit. `fail` and `insufficient` mean the same thing whenever they were
  // written.
  const blockingFlag = pick('blocking');
  const blocking = BLOCKING_VERDICTS.includes(verdict) || Boolean(blockingFlag);

  const overrideReason = text(pick('override_reason'));
  const overriddenAt = text(pick('overridden_at'));
  const overridden =
    Boolean(pick('overridden')) ||
    Boolean(overrideReason) ||
    Boolean(overriddenAt);

  const currentFlag = pick('current');
  const currentSnapshotFlag = pick('verdict_current');
  const stale =
    Boolean(pick('stale')) ||
    currentFlag === false ||
    currentSnapshotFlag === false;

  const checks = asChecks(
    document.checks != null ? document.checks : nested.checks,
  );
  // The link snapshot pre-sorts the checks that matter, worst first, and sends
  // only those; a raw verdict sends all sixteen and leaves the picking here.
  const snapshotFailing = asChecks(pick('failing_checks'));
  const snapshotCautions = asChecks(pick('caution_checks'));
  const namedFailures = pick('failed_checks');
  const named: string[] = Array.isArray(namedFailures)
    ? namedFailures.map(text).filter(Boolean)
    : [];
  // The checks worth quoting: the ones that produced the state. Fall back to
  // the names the summary lists when the full check rows are not served.
  const blockingChecks = checks.filter(check =>
    BLOCKING_VERDICTS.includes(check.verdict),
  );
  const warnChecks = checks.filter(check => check.verdict === 'warn');
  const failingChecks: AnswerKeyCheck[] = blocking
    ? snapshotFailing.length
      ? snapshotFailing
      : blockingChecks.length
        ? blockingChecks
        : named.map(key => ({ key, verdict: 'fail' }))
    : snapshotCautions.length
      ? snapshotCautions
      : warnChecks;

  // A STALE FAIL IS NOT A CAUTION, AND THE ORDER HERE IS WHAT SAYS SO.
  //
  // A caution is a warning: somebody measured this answer key, it is fit to
  // grade, and here is the wrinkle. A stale fail is the opposite kind of
  // statement -- the last measurement FAILED, and it was taken on a different
  // version, so nobody can tell you whether the version in front of you is
  // fit. "We do not know" cannot be softened into "fit, with a wrinkle", so a
  // blocking verdict keeps its consequence however old it is; `stale` then
  // changes the WORDS (see `gateHeadline`), not the state. Rendering it as a
  // caution is exactly the bug: the rating band survived and a failed answer
  // key printed a hiring signal.
  let state: AnswerKeyGateState = 'clear';
  if (!recorded) state = 'unrecorded';
  else if (blocking && !overridden) state = 'blocked';
  else if (blocking) state = 'overridden';
  // A non-blocking grading of different content is not evidence about what is
  // stored now either, so it is said out loud rather than passing silently.
  else if (stale || verdict === 'warn') state = 'caution';

  // The backend answers this directly on the link snapshot. Where it does, it
  // wins: the label a report prints and the sentence above it must come from
  // the same decision, or the screen argues with itself.
  const ratingIsEvidence = pick('rating_is_evidence');
  // Where the backend does NOT answer it, derive it the way the backend does
  // (`rating_is_evidence`, services/attach_gate.py): a rating is evidence only
  // when a CURRENT grading either cleared the audit or was signed for. Stale
  // answers the same as failed here, because to the person reading the number
  // they mean the same thing -- nobody has shown that this scale works.
  const suppress =
    ratingIsEvidence != null
      ? !ratingIsEvidence
      : state === 'blocked' || (stale && !overridden);

  // Carried on every link snapshot since the attach and rendered by nothing:
  // a candidate marked on GOLD whose Markets board never drew it. Live, link
  // 15 has carried this in a payload the page already fetched.
  const visibility = pick('instrument_visibility');
  const invisibleInstruments =
    isObject(visibility) && Array.isArray(visibility.problems)
      ? (visibility.problems as unknown[]).map(text).filter(Boolean)
      : [];

  // WHAT THE KEY IS TODAY, which is not what the verdict is about. The verdict
  // describes the grading; `key_status_now` describes the key now. A key that
  // passed its audit and was then replaced is retired AND clear, so this rides
  // alongside the state rather than becoming one.
  const retired =
    text(pick('key_status_now')).toLowerCase() === 'retired' ||
    pick('key_retired') === true;
  const retiredAt = text(pick('key_retired_at'));
  const supersededBy = text(pick('key_superseded_by'));
  const retiredNote = retired
    ? text(pick('key_retired_sentence')) ||
      retirementSentence(retiredAt, supersededBy)
    : '';

  return {
    state,
    retired,
    retiredAt,
    supersededBy,
    retiredNote,
    invisibleInstruments,
    code: text(pick('code') || pick('scenario_code') || pick('scenario')),
    headline: text(pick('headline')),
    passBarSentence: text(pick('pass_bar_sentence')),
    launchBlocked: Boolean(pick('launch_blocked')),
    launchBlockReason: text(pick('launch_block_reason')),
    verdict,
    blocking,
    overridden,
    overriddenBy: text(pick('overridden_by')),
    overriddenAt,
    overrideReason,
    gateVersion: text(pick('gate_version')),
    computedAt: text(pick('computed_at')),
    stale,
    failingChecks,
    suppressRating: suppress,
    suppressLegend:
      ratingIsEvidence != null ? suppress : suppress || state === 'unrecorded',
  };
};

/** The rating band shown in place of "Positive / coachable" when the answer
 *  key that produced the score failed its own quality audit. */
export const UNRATED_LABEL = 'Indicative only — not rated';

/**
 * The consequence sentence, in the words the trainer needs: the direction of
 * the error is asymmetric, and saying so is the whole point of the banner.
 */
export const BLOCKED_CONSEQUENCE =
  'A low score here is meaningful, but a passing score is not evidence of ' +
  'skill. Do not use these numbers in a hiring decision.';

/** The banner heading for a score graded by a key that is now out of service. */
export const RETIRED_HEADLINE =
  'The answer key behind these scores has been retired';

/** The same fact in a table cell or a picker row. */
export const RETIRED_TAG = 'Answer key retired';

export const INVISIBLE_INSTRUMENTS_HEADLINE =
  'This candidate was marked on instruments their Markets screen never showed';

export const UNRECORDED_CONSEQUENCE =
  'Nobody has measured whether this answer key separates a candidate who ' +
  'reads the news from one who clicks. Treat these scores as unverified.';

export const STALE_NOTE =
  'The check that produced this verdict graded a different version of the ' +
  'answer key.';

/**
 * The consequence of a failure nobody can re-confirm. Deliberately NOT the
 * blocked sentence: there, a low score is still meaningful, because the
 * failing check describes the answer key in front of you. Here it does not
 * describe anything in front of you, so neither end of the scale is evidence.
 */
export const STALE_BLOCKED_CONSEQUENCE =
  'That check has not been re-run since, so nobody can say whether this ' +
  'answer key still fails. Treat neither a high nor a low score here as ' +
  'evidence, and do not use these numbers in a hiring decision.';

/** The sentence that follows the headline, per state. */
export const gateConsequence = (gate: AnswerKeyGate): string => {
  if (gate.state === 'unrecorded') return UNRECORDED_CONSEQUENCE;
  if (gate.state !== 'blocked') return '';
  return gate.stale ? STALE_BLOCKED_CONSEQUENCE : BLOCKED_CONSEQUENCE;
};

/** Headline per state. Written for a trader, not for an engineer. */
export const gateHeadline = (gate: AnswerKeyGate): string => {
  switch (gate.state) {
    case 'blocked':
      // A stale failure must not be read out as a finding about what is
      // stored now. It is a failure that graded something else.
      if (gate.stale) {
        return gate.code
          ? `Answer key ${gate.code} failed its last quality audit, and that audit graded a different version of it`
          : 'The answer key behind these scores failed its last quality audit, and that audit graded a different version of it';
      }
      return gate.code
        ? `Answer key ${gate.code} did not pass its quality audit`
        : 'The answer key behind these scores did not pass its quality audit';
    case 'overridden':
      return `${gate.overriddenBy || 'An administrator'} approved this answer key for grading despite a failed quality audit`;
    case 'caution':
      return 'The quality audit raised a warning on this answer key';
    case 'unrecorded':
      return 'No quality audit on record for this answer key';
    default:
      return '';
  }
};

/** One short line for a table cell or a picker row. */
export const gateShortLabel = (gate: AnswerKeyGate): string => {
  switch (gate.state) {
    case 'blocked':
      return gate.stale
        ? 'Last quality audit failed, and it is out of date'
        : 'Answer key failed its quality audit';
    case 'overridden':
      return 'Failed quality audit, approved by an administrator';
    case 'caution':
      return 'Quality audit warning';
    case 'unrecorded':
      return 'Never audited for quality';
    default:
      return 'Quality audit passed';
  }
};
